import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../../lib/api";
import type { ShipmentIntentPublic } from "@shared/types";

const POLL_INTERVAL_MS = 10_000;
const POLL_TIMEOUT_MS = 10 * 60_000; // 10 minutes — bounded, never polls forever

interface Props {
  shipment: ShipmentIntentPublic;
  onUpdated: (updated: ShipmentIntentPublic) => void;
}

// Step 9b — shown after the sender has been sent to a provider-hosted
// checkout (currently only Parcel2Go) and is back on (or has reloaded)
// this page. Parcel2Go has no webhook and no configurable return URL
// (confirmed — see docs/parcel2go-payment-validation.md §H), so polling
// is the only way this app ever discovers payment cleared. Mirrors the
// already-validated admin sandbox tool's polling UX
// (src/admin/Parcel2GoSandboxTest.tsx).
export default function AwaitingPaymentStep({ shipment, onUpdated }: Props) {
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pollStartedAt = useRef(Date.now());

  const checkNow = useCallback(async () => {
    setChecking(true);
    setError(null);
    try {
      const updated = await api.pollPayment(shipment.id);
      onUpdated(updated);
    } catch {
      setError("Couldn't check payment status just now — we'll try again shortly.");
    } finally {
      setChecking(false);
    }
  }, [shipment.id, onUpdated]);

  // Auto-poll while waiting, bounded by POLL_TIMEOUT_MS, stops once the
  // shipment moves on (this component unmounts once status changes).
  useEffect(() => {
    if (Date.now() - pollStartedAt.current > POLL_TIMEOUT_MS) return;
    const timer = setTimeout(checkNow, POLL_INTERVAL_MS);
    return () => clearTimeout(timer);
  }, [checkNow]);

  const timedOut = Date.now() - pollStartedAt.current > POLL_TIMEOUT_MS;
  const redirectUrl = shipment.checkout?.redirectUrl;

  return (
    <div className="stack fade-in-up">
      <div>
        <h1>Waiting for payment</h1>
        <p style={{ color: "var(--color-text-muted)" }}>
          You're paying Parcel2Go directly on their own page — your card details are never handled by this site.
        </p>
      </div>

      <div className="banner banner-warn">
        <span>
          {timedOut
            ? "Still waiting to hear back — use the button below to check again."
            : "Waiting for Parcel2Go to confirm your payment… (checking automatically)"}
        </span>
      </div>

      {error && (
        <div className="banner banner-danger" role="alert">
          <span>{error}</span>
        </div>
      )}

      <div className="stack-sm">
        {redirectUrl && (
          <a className="btn btn-secondary" href={redirectUrl} target="_blank" rel="noopener noreferrer">
            Reopen Parcel2Go checkout
          </a>
        )}
        <button className="btn btn-primary" onClick={checkNow} disabled={checking}>
          {checking ? "Checking…" : "Check payment status now"}
        </button>
      </div>

      <p className="field-help">
        Already paid but this hasn't updated? Parcel2Go doesn't send us an instant notification, so it can take a few
        seconds — try "Check payment status now" above.
      </p>
    </div>
  );
}
